"use client";

import { useState } from "react";
import { doc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTranslations } from "@/contexts/I18nContext";
import type { ResponsibilityClaim } from "@/lib/models";

interface RescheduleModalProps {
  claim: ResponsibilityClaim;
  householdId: string;
  requestedBy: string;
  onClose: () => void;
}

export function RescheduleModal({ claim, householdId, requestedBy, onClose }: RescheduleModalProps) {
  const t = useTranslations();
  const [proposedTime, setProposedTime] = useState("");
  const [proposedDate, setProposedDate] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!claim.uid || !proposedTime) return;

    setSubmitting(true);
    try {
      const ref = doc(db, `households/${householdId}/responsibilityClaims`, claim.uid);

      // Build the proposed time as a Timestamp from today + time
      const dateStr = proposedDate || new Date().toISOString().split("T")[0];
      const proposedDateTime = new Date(`${dateStr}T${proposedTime}:00`);

      const updates: Record<string, unknown> = {
        priorStatus: claim.status,
        status: "reschedule_pending",
        rescheduleRequestedBy: requestedBy,
        rescheduleRequestedAt: Timestamp.now(),
        rescheduleProposedTime: Timestamp.fromDate(proposedDateTime),
        rescheduleNote: note || null,
      };

      if (proposedDate) {
        updates.rescheduleProposedDate = Timestamp.fromDate(new Date(`${proposedDate}T00:00:00`));
      }

      await updateDoc(ref, updates);
      onClose();
    } catch (err) {
      console.error("Failed to request reschedule:", err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-50 w-full max-w-sm mx-4 mb-4 sm:mb-0 bg-white rounded-2xl shadow-xl overflow-hidden text-gray-900">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-center">{t("responsibilities.reschedule.title")}</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Proposed time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.reschedule.timeLabel")}
            </label>
            <input
              type="time"
              value={proposedTime}
              onChange={(e) => setProposedTime(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Optional proposed date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.reschedule.dateLabel")}
            </label>
            <input
              type="date"
              value={proposedDate}
              onChange={(e) => setProposedDate(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t("responsibilities.reschedule.note.label")}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("responsibilities.reschedule.note.placeholder")}
              rows={3}
              className="w-full rounded-lg border border-gray-200 bg-white text-gray-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[color:var(--accent-color)] resize-none"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              {t("responsibilities.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={!proposedTime || submitting}
              className="flex-1 py-2 rounded-lg text-sm font-medium text-white bg-[color:var(--accent-color)] disabled:opacity-40 transition-opacity"
            >
              {submitting
                ? t("responsibilities.reschedule.submitting")
                : t("responsibilities.reschedule.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
